import { EventEmitter } from "eventemitter3";
import { Mutex } from "async-mutex";
import {
  NiimbotProtocol,
  ConnectResult,
  NiimbotPacket,
  PacketParser,
  PrinterErrorCode,
  ResponseCommandId,
  SoundSettingsItemType,
} from "../packets";
import { PrinterModelMeta, getPrinterMetaById } from "../printer_models";
import {
  ClientEventMap,
  PacketSentEvent,
  PrinterInfoFetchedEvent,
  HeartbeatEvent,
  HeartbeatFailedEvent,
  PacketReceivedEvent,
  RawPacketReceivedEvent,
  RfidInfoFetchedEvent,
} from "../events";
import { findPrintTask, PrintTaskName } from "../print_tasks";
import { Utils, Validators } from "../utils";
import { CombinedRfidInfo, HeartbeatData, PrinterInfo, PrintError } from "../packets/dto";
import { NiimbotClientType } from ".";

/**
 * Represents the connection result information.
 *
 * @category Client
 */
export type ConnectionInfo = {
  deviceName?: string;
  result: ConnectResult;
};

export const NIIMBOT_CLIENT_DEFAULTS = {
  packetIntervalMs: 10,
  heartbeatIntervalMs: 2_000,
};

export const createPrinterInfo = (): PrinterInfo => {
  return {
    settings: {
      connectionSound: false,
      powerSound: false,
    },
  };
};

/**
 * Abstract class representing a client with common functionality for interacting with a printer.
 * Hardware interface must be defined after extending this class.
 *
 * @category Client
 */
export abstract class NiimbotAbstractClient extends EventEmitter<ClientEventMap> {
  public readonly protocol: NiimbotProtocol;
  protected printerInfo: PrinterInfo = createPrinterInfo();
  protected rfidInfo: CombinedRfidInfo = {};
  protected heartbeatData: HeartbeatData = {};
  private heartbeatTimer?: NodeJS.Timeout;
  private heartbeatFails: number = 0;
  private heartbeatIntervalMs: number = NIIMBOT_CLIENT_DEFAULTS.heartbeatIntervalMs;
  private heartbeatAutoStart: boolean = true;
  private heartbeatMaxFails: number = 5;
  private skipNextHeartbeatRfidCheck = false;
  protected mutex: Mutex = new Mutex();
  protected debug: boolean = false;
  private packetBuf: Uint8Array = new Uint8Array();
  private fetchRfidOnPrintEnd: boolean = true;

  /** @see https://github.com/MultiMote/niimblue/issues/5 */
  protected packetIntervalMs: number = NIIMBOT_CLIENT_DEFAULTS.packetIntervalMs;

  constructor() {
    super();
    this.protocol = new NiimbotProtocol(this);

    this.on("connect", () => {
      if (this.heartbeatAutoStart) {
        this.startHeartbeat();
      }
    });

    this.on("disconnect", () => {
      this.stopHeartbeat();
      this.packetBuf = new Uint8Array();
      this.printerInfo = createPrinterInfo();
      this.rfidInfo = {};
      this.heartbeatData = {};
    });

    this.on("printend", async () => {
      if (this.fetchRfidOnPrintEnd) {
        await this.fetchRfidInfo();
      }
    });
  }

  public setHeartbeatAutoStart(value: boolean) {
    this.heartbeatAutoStart = value;
  }

  public getHeartbeatAutoStart(): boolean {
    return this.heartbeatAutoStart;
  }

  /**
   * Connect to printer port.
   **/
  public abstract connect(): Promise<ConnectionInfo>;

  /**
   * Disconnect from printer port.
   **/
  public abstract disconnect(): Promise<void>;

  /**
   * Check if the client is connected.
   */
  public abstract isConnected(): boolean;

  /**
   * Send packet and wait for response for {@link timeoutMs} milliseconds.
   *
   * If {@link NiimbotPacket.validResponseIds() validResponseIds} is defined, it will wait for packet with this command id.
   *
   * @throws {@link PrintError} when {@link ResponseCommandId.In_PrintError} or {@link ResponseCommandId.In_NotSupported} received.
   *
   * @returns {NiimbotPacket} Packet object.
   */
  public async sendPacketWaitResponse(packet: NiimbotPacket, timeoutMs: number = 1000): Promise<NiimbotPacket> {
    return this.mutex.runExclusive(async () => {
      await this.sendPacket(packet, true);

      if (packet.oneWay) {
        return new NiimbotPacket(ResponseCommandId.In_Invalid, []); // or undefined is better?
      }

      return this.waitForPacket(packet.validResponseIds, true, timeoutMs);
    });
  }

  /**
   * Send wait for response for {@link timeoutMs} milliseconds.
   *
   * If {@link ids} is set, it will wait for packet with this command ids.
   *
   * @throws {@link PrintError} when {@link ResponseCommandId.In_PrintError} or {@link ResponseCommandId.In_NotSupported} received and {@link catchErrorPackets} is true.
   *
   * @returns {NiimbotPacket} Packet object.
   */
  public async waitForPacket(
    ids: ResponseCommandId[] = [],
    catchErrorPackets: boolean = true,
    timeoutMs: number = 1000,
  ): Promise<NiimbotPacket> {
    return new Promise((resolve, reject) => {
      let timeout: NodeJS.Timeout | undefined = undefined;

      const listener = (evt: PacketReceivedEvent) => {
        const pktIn = evt.packet;
        const cmdIn = pktIn.command as ResponseCommandId;

        if (
          ids.length === 0 ||
          ids.includes(cmdIn) ||
          (catchErrorPackets && [ResponseCommandId.In_PrintError, ResponseCommandId.In_NotSupported].includes(cmdIn))
        ) {
          clearTimeout(timeout);
          this.off("packetreceived", listener);

          if (cmdIn === ResponseCommandId.In_PrintError) {
            Validators.arrayLengthEquals(pktIn.data, 1);
            const errorName = PrinterErrorCode[pktIn.data[0]] ?? "unknown";
            reject(new PrintError(`Print error ${pktIn.data[0]}: ${errorName}`, pktIn.data[0]));
          } else if (cmdIn === ResponseCommandId.In_NotSupported) {
            reject(new PrintError("Feature not supported", 0));
          } else {
            resolve(pktIn);
          }
        }
      };

      timeout = setTimeout(() => {
        this.off("packetreceived", listener);
        reject(new Error(`Timeout waiting response (waited for ${Utils.bufToHex(ids, ", ")})`));
      }, timeoutMs ?? 1000);

      this.on("packetreceived", listener);
    });
  }

  /**
   * Convert raw bytes to packet objects and fire events. Defragmentation included.
   * @param data Bytes to process.
   */
  protected processRawPacket(data: DataView | Uint8Array) {
    if (data.byteLength === 0) {
      return;
    }

    if (data instanceof DataView) {
      data = new Uint8Array(data.buffer);
    }

    this.packetBuf = Utils.u8ArrayAppend(this.packetBuf, data);

    if (this.packetBuf.length > 1 && !Utils.hasSubarrayAtPos(this.packetBuf, NiimbotPacket.HEAD, 0)) {
      console.warn("Dropping invalid buffer", Utils.bufToHex(this.packetBuf));
      this.packetBuf = new Uint8Array();
    }

    try {
      const packets: NiimbotPacket[] = PacketParser.parsePacketBundle(this.packetBuf);

      if (packets.length > 0) {
        this.emit("rawpacketreceived", new RawPacketReceivedEvent(this.packetBuf));

        packets.forEach((p) => {
          this.emit("packetreceived", new PacketReceivedEvent(p));
        });

        this.packetBuf = new Uint8Array();
      }
    } catch (_e) {
      if (this.debug) {
        console.info(`Incomplete packet, ignoring:${Utils.bufToHex(this.packetBuf)}`, _e);
      }
    }
  }

  /**
   * Send raw bytes to the printer port.
   *
   * @param data Bytes to send.
   * @param force Ignore mutex lock. It used internally and you should avoid using it.
   */
  public abstract sendRaw(data: Uint8Array, force?: boolean): Promise<void>;

  public async sendPacket(packet: NiimbotPacket, force?: boolean) {
    await this.sendRaw(packet.toBytes(), force);
    this.emit("packetsent", new PacketSentEvent(packet));
  }

  /**
   * Send "connect" packet and fetch the protocol version.
   **/
  protected async connectNegotiate(): Promise<void> {
    const result = await this.protocol.connectNegotiate();
    this.printerInfo.connectResult = result.connectResult;
    this.printerInfo.protocolVersion = result.protocolVersion;
    this.printerInfo.supportColor = result.supportColor;
  }

  /**
   * Fetch printer information and store it
   */
  public async fetchPrinterInfo(): Promise<PrinterInfo> {
    const safeGet = <T>(promise: Promise<T>, msg: string) =>
      promise.catch((e) => {
        console.warn(`Unable to get ${msg} (${e})`);
        return undefined;
      });

    this.printerInfo.modelId = await this.protocol.getPrinterModel();
    this.printerInfo.serial = await safeGet(this.protocol.getPrinterSerialNumber(), "serial number");
    this.printerInfo.mac = await safeGet(this.protocol.getPrinterBluetoothMacAddress(), "bluetooth address");
    this.printerInfo.batteryPercents = await safeGet(this.protocol.getBatteryChargeLevel(), "charge level");
    this.printerInfo.autoShutdownTime = await safeGet(this.protocol.getAutoShutDownTime(), "auto shutdown time");

    this.printerInfo.settings.connectionSound =
      (await safeGet(this.protocol.isSoundEnabled(SoundSettingsItemType.BluetoothConnectionSound), "sound value")) ??
      false;

    this.printerInfo.settings.powerSound =
      (await safeGet(this.protocol.isSoundEnabled(SoundSettingsItemType.PowerSound), "sound value")) ?? false;

    try {
      const i = await this.protocol.heartbeatPrinterInfo();
      this.printerInfo.printheadWidth = i.printheadWidth;
      this.printerInfo.hardwareVersion = i.hardwareVersion;
      this.printerInfo.softwareVersion = i.softwareVersion;
      this.printerInfo.resolutionClass = i.resolutionClass;
    } catch (e) {
      console.warn(`Unable to get printhead width and some other info (${e})`);
      this.printerInfo.hardwareVersion = await safeGet(this.protocol.getHardwareVersion(), "hardware version");
      this.printerInfo.softwareVersion = await safeGet(this.protocol.getSoftwareVersion(), "software version");
    }

    if (this.printerInfo.protocolVersion !== undefined && this.printerInfo.protocolVersion >= 4) {
      this.printerInfo.capabilities = await safeGet(this.protocol.gePrinterCapabilities(), "printer capabilities");
    }

    this.emit("printerinfofetched", new PrinterInfoFetchedEvent(this.printerInfo));
    return this.printerInfo;
  }

  /**
   * Fetch label and ribbon RFID information and store it. Do not throws exceptions.
   */
  public async fetchRfidInfo(): Promise<CombinedRfidInfo> {
    let info: CombinedRfidInfo = {};

    try {
      info.labelRfidInfo = await this.protocol.rfidInfo();
    } catch (e) {
      console.warn("Unable to fetch RFID info", e);
    }

    try {
      info.paperInfo = await this.protocol.getPaperInfo();
    } catch (e) {
      // ignore
    }

    try {
      info.ribbonRfidInfo = await this.protocol.rfidInfo2();
    } catch (e) {
      // ignore
    }

    this.rfidInfo = info;

    this.emit("rfidinfofetched", new RfidInfoFetchedEvent(info));
    return info;
  }

  /**
   * Calls {@link connectNegotiate} (exceptions are not ignored) then calls {@link fetchPrinterInfo} and {@link fetchRfidInfo} (exceptions ignored)
   * @param cleanup cleanup/disconnect function, called when initialNegotiate fails
   */
  protected async negotiateAndGetPrinterInfo(cleanup?: () => void) {
    try {
      await this.connectNegotiate();
    } catch (e) {
      if (cleanup) cleanup();
      throw new Error(`Unable to perform initial negotiate: ${e}`);
    }

    try {
      await this.fetchPrinterInfo();
    } catch (e) {
      if (cleanup) cleanup();
      throw new Error(`Unable to fetch printer info: ${e}`);
    }

    await this.fetchRfidInfo();

    this.skipNextHeartbeatRfidCheck = true;
  }

  /**
   * Fetch heartbeat information and store it. Emits `heartbeat`/`heartbeatfailed` event.  Do not throws exceptions.
   */
  public async fetchHeartbeatData(): Promise<HeartbeatData | undefined> {
    try {
      const data = await this.protocol.heartbeat();
      this.heartbeatFails = 0;
      this.heartbeatReceived(data);
      this.emit("heartbeat", new HeartbeatEvent(data));
      this.heartbeatData = data;
      return data;
    } catch (e) {
      console.error(e);
      this.heartbeatFails++;
      this.emit("heartbeatfailed", new HeartbeatFailedEvent(this.heartbeatFails));

      if (this.heartbeatFails > 0 && this.heartbeatFails >= this.heartbeatMaxFails) {
        await this.disconnect();
      }
    }

    return undefined;
  }

  public async setSoundEnabled(soundType: SoundSettingsItemType, value: boolean) {
    await this.protocol.setSoundEnabled(soundType, value);

    if (soundType === SoundSettingsItemType.BluetoothConnectionSound) {
      this.printerInfo.settings.connectionSound = value;
    }

    if (soundType === SoundSettingsItemType.PowerSound) {
      this.printerInfo.settings.powerSound = value;
    }

    this.emit("printerinfofetched", new PrinterInfoFetchedEvent(this.printerInfo));
  }

  /**
   * Get the stored information about the printer. Call {@link fetchPrinterInfo} to update.
   */
  public getPrinterInfo(): PrinterInfo {
    return this.printerInfo;
  }

  /**
   * Get the stored information about RFID tags. Call {@link fetchRfidInfo} to update.
   */
  public getRfidInfo(): CombinedRfidInfo {
    return this.rfidInfo;
  }

  /**
   * Get the stored heartbeat information about RFID tags. Call {@link fetchHeartbeatData} to update.
   */
  public getHeartbeatData(): HeartbeatData {
    return this.heartbeatData;
  }

  /**
   * Set interval for {@link startHeartbeat}.
   *
   * @param intervalMs Heartbeat interval, default is 1000ms
   */
  public setHeartbeatInterval(intervalMs: number): void {
    this.heartbeatIntervalMs = intervalMs;
  }

  public getHeartbeatInterval(): number {
    return this.heartbeatIntervalMs;
  }

  /**
   * Starts the heartbeat timer, "heartbeat" is emitted after packet received.
   *
   * If you need to change interval, call {@link setHeartbeatInterval} before.
   */
  public startHeartbeat(): void {
    this.heartbeatFails = 0;
    this.stopHeartbeat();
    this.heartbeatTimer = setInterval(() => this.fetchHeartbeatData(), this.heartbeatIntervalMs);
  }

  private heartbeatReceived(data: HeartbeatData) {
    if (data.batteryPercents) {
      this.printerInfo.batteryPercents = data.batteryPercents;
    }

    if (this.skipNextHeartbeatRfidCheck) {
      this.skipNextHeartbeatRfidCheck = false;
    } else {
      const paperRfidChanged = this.heartbeatData?.paperRfidSuccess !== data?.paperRfidSuccess;
      const ribbonRfidChanged = this.heartbeatData?.ribbonRfidSuccess !== data?.ribbonRfidSuccess;
      const lidChanged = this.heartbeatData?.lidClosed !== data?.lidClosed;

      if (lidChanged || paperRfidChanged || ribbonRfidChanged) {
        this.fetchRfidInfo();
      }
    }
  }

  /**
   * Stops the heartbeat by clearing the interval timer.
   */
  public stopHeartbeat(): void {
    clearInterval(this.heartbeatTimer);
    this.heartbeatTimer = undefined;
  }

  /**
   * Checks if the heartbeat timer has been started.
   */
  public isHeartbeatStarted(): boolean {
    return this.heartbeatTimer === undefined;
  }

  /**
   * Get printer capabilities based on the printer model. Model library is hardcoded.
   **/
  public getModelMetadata(): PrinterModelMeta | undefined {
    if (this.printerInfo.modelId === undefined) {
      return undefined;
    }
    return getPrinterMetaById(this.printerInfo.modelId);
  }

  /**
   * Determine print task version if any.
   **/
  public getPrintTaskType(): PrintTaskName | undefined {
    const meta = this.getModelMetadata();

    if (meta === undefined) {
      return undefined;
    }

    return findPrintTask(meta.model, this.getPrinterInfo().protocolVersion);
  }

  /**
   * Set the interval between packets in milliseconds.
   */
  public setPacketInterval(milliseconds: number) {
    this.packetIntervalMs = milliseconds;
  }

  public getPacketInterval(): number {
    return this.packetIntervalMs;
  }

  /**
   * Enable some debug information logging.
   */
  public setDebug(value: boolean) {
    this.debug = value;
  }

  public getDebug(): boolean {
    return this.debug;
  }

  /**
   * Set max failed heartbeat attempts to disconnect. Use 0 to disable.
   */
  public setHeartbeatMaxFails(value: number) {
    this.heartbeatMaxFails = value;
  }

  public getHeartbeatMaxFails(): number {
    return this.heartbeatMaxFails;
  }

  public isFetchRfidOnPrintEnd(): boolean {
    return this.fetchRfidOnPrintEnd;
  }

  public setFetchRfidOnPrintEnd(value: boolean) {
    this.fetchRfidOnPrintEnd = value;
  }

  public abstract getType(): NiimbotClientType;
}
